/* =====================================================================
 * routeConfig — 경로 ↔ WorkspaceMode 매핑 + SEO 메타 데이터
 *
 * 새 모드/경로 추가 시 ROUTE_ENTRIES 배열에 한 줄만 추가하면
 * 라우팅 · 사이드바 · SEO가 자동으로 반영됩니다.
 * ===================================================================== */

import type { WorkspaceMode } from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* SEO metadata per route                                              */
/* ------------------------------------------------------------------ */

export type RouteSEOTitleKey =
  | 'nav.skillMarketplace'
  | 'nav.dashboard'
  | 'nav.translation'
  | 'nav.memory'
  | 'nav.agent'
  | 'nav.liveAgent'
  | 'nav.document'
  | 'nav.docAgent'
  | 'nav.storage'
  | 'nav.message'
  | 'nav.widget'
  | 'nav.feed'
  | 'nav.tools'
  | 'nav.plugin'
  | 'nav.appManage'
  | 'nav.teamManage'
  | 'nav.skillCreate'
  | 'workspace:publicSeo.pricingTitle'
  | 'workspace:publicSeo.checkoutTitle'
  | 'workspace:publicSeo.contactSalesTitle'
  | 'workspace:publicSeo.termsTitle'
  | 'workspace:publicSeo.privacyTitle'
  | 'workspace:publicSeo.marketingConsentTitle';

export type RouteSEODescriptionKey =
  | 'workspace:seo.skillMarketplace'
  | 'workspace:seo.dashboard'
  | 'workspace:seo.translation'
  | 'workspace:seo.memory'
  | 'workspace:seo.agent'
  | 'workspace:seo.liveAgent'
  | 'workspace:seo.document'
  | 'workspace:seo.docAgent'
  | 'workspace:seo.storage'
  | 'workspace:seo.message'
  | 'workspace:seo.widget'
  | 'workspace:seo.feed'
  | 'workspace:seo.tools'
  | 'workspace:seo.plugin'
  | 'workspace:seo.appManage'
  | 'workspace:seo.teamManage'
  | 'workspace:seo.skillCreate'
  | 'workspace:publicSeo.pricingDesc'
  | 'workspace:publicSeo.checkoutDesc'
  | 'workspace:publicSeo.contactSalesDesc'
  | 'workspace:publicSeo.termsDesc'
  | 'workspace:publicSeo.privacyDesc'
  | 'workspace:publicSeo.marketingConsentDesc';

export interface RouteSEO {
  titleKey: RouteSEOTitleKey;
  descriptionKey: RouteSEODescriptionKey;
  ogTitle?: string;
  ogDescription?: string;
}

/* --------------------------------------------------------------------
 * Tenant-backed identity constants.
 *
 * These were previously hard-coded strings.  They now read from the
 * resolved `TenantManifest` so B2B tenants can rebrand without patching
 * every caller; the 150+ existing imports remain unchanged because the
 * exported names and their resolved values stay identical for B2C.
 * -------------------------------------------------------------------- */
import { absoluteAssetUrl, resolveTenantDefaultPath } from '@auw/tenant';
import { resolveTenantManifest } from '@/tenant';

const activeTenant = resolveTenantManifest();

export const SITE_URL = activeTenant.identity.siteUrl;
export const DEFAULT_OG_IMAGE = absoluteAssetUrl(
  activeTenant,
  activeTenant.assets.defaultOgImage,
);

/**
 * Tenant-resolved fallback path used by `modeToPath` / `modeToPathWithId`
 * when a WorkspaceMode has no registered route entry. Pre-Phase-11-3a
 * this was hard-coded `'/widgets'`; now every caller routes through the
 * tenant's `workspace.defaultPath` (B2C keeps `/widgets`, on-prem
 * tenants get their own landing). Resolved at module-load time so
 * repeated calls stay O(1) and do not rebuild the tenant graph.
 */
const TENANT_DEFAULT_PATH = resolveTenantDefaultPath(activeTenant);

/* ------------------------------------------------------------------ */
/* Route entry                                                         */
/* ------------------------------------------------------------------ */

export interface RouteEntry {
  /** URL path prefix (e.g. '/agents') */
  path: string;
  /** chatStore.activeMode 값 */
  mode: WorkspaceMode;
  /** `:id` 파라미터를 지원하는 라우트 여부 */
  hasIdParam: boolean;
  /** SEO 메타 데이터 */
  seo: RouteSEO;
}

/* ------------------------------------------------------------------ */
/* 워크스페이스 라우트 목록                                                */
/* ------------------------------------------------------------------ */

export const ROUTE_ENTRIES: RouteEntry[] = [
  {
    path: '/agents',
    mode: 'agent',
    hasIdParam: true,
    seo: {
      titleKey: 'nav.agent',
      descriptionKey: 'workspace:seo.agent',
    },
  },
  {
    path: '/live-agents',
    mode: 'live-agent',
    hasIdParam: true,
    seo: {
      titleKey: 'nav.liveAgent',
      descriptionKey: 'workspace:seo.liveAgent',
    },
  },
  {
    path: '/docs',
    mode: 'document',
    hasIdParam: true,
    seo: {
      titleKey: 'nav.document',
      descriptionKey: 'workspace:seo.document',
    },
  },
  {
    path: '/doc-agent',
    mode: 'doc-agent',
    hasIdParam: true,
    seo: {
      titleKey: 'nav.docAgent',
      descriptionKey: 'workspace:seo.docAgent',
    },
  },
  { path: '/memory', mode: 'memory', hasIdParam: false, seo: { titleKey: 'nav.memory', descriptionKey: 'workspace:seo.memory' } },
  { path: '/translation', mode: 'translation', hasIdParam: false, seo: { titleKey: 'nav.translation', descriptionKey: 'workspace:seo.translation' } },
  { path: '/dashboard', mode: 'dashboard', hasIdParam: false, seo: { titleKey: 'nav.dashboard', descriptionKey: 'workspace:seo.dashboard' } },
  { path: '/marketplace/skills', mode: 'skill-marketplace', hasIdParam: false, seo: { titleKey: 'nav.skillMarketplace', descriptionKey: 'workspace:seo.skillMarketplace' } },
  {
    path: '/storage',
    mode: 'storage',
    hasIdParam: false,
    seo: {
      titleKey: 'nav.storage',
      descriptionKey: 'workspace:seo.storage',
    },
  },
  {
    path: '/messages',
    mode: 'message',
    hasIdParam: true,
    seo: {
      titleKey: 'nav.message',
      descriptionKey: 'workspace:seo.message',
    },
  },
  {
    path: '/widgets',
    mode: 'widget',
    hasIdParam: false,
    seo: {
      titleKey: 'nav.widget',
      descriptionKey: 'workspace:seo.widget',
    },
  },
  {
    path: '/feeds',
    mode: 'feed',
    hasIdParam: false,
    seo: {
      titleKey: 'nav.feed',
      descriptionKey: 'workspace:seo.feed',
    },
  },
  {
    path: '/tools',
    mode: 'tools',
    hasIdParam: false,
    seo: {
      titleKey: 'nav.tools',
      descriptionKey: 'workspace:seo.tools',
    },
  },
  {
    path: '/plugins',
    mode: 'plugin',
    hasIdParam: false,
    seo: {
      titleKey: 'nav.plugin',
      descriptionKey: 'workspace:seo.plugin',
    },
  },
  {
    path: '/settings/apps',
    mode: 'app-manage',
    hasIdParam: false,
    seo: {
      titleKey: 'nav.appManage',
      descriptionKey: 'workspace:seo.appManage',
    },
  },
  {
    path: '/settings/team',
    mode: 'admin-page',
    hasIdParam: false,
    seo: {
      titleKey: 'nav.teamManage',
      descriptionKey: 'workspace:seo.teamManage',
    },
  },
  {
    path: '/settings/skills',
    mode: 'skill-create',
    hasIdParam: false,
    seo: {
      titleKey: 'nav.skillCreate',
      descriptionKey: 'workspace:seo.skillCreate',
    },
  },
];

/* ------------------------------------------------------------------ */
/* 공개 라우트 SEO                                                      */
/* ------------------------------------------------------------------ */

export const PUBLIC_ROUTE_SEO: Record<string, RouteSEO> = {
  '/pricing': {
    titleKey: 'workspace:publicSeo.pricingTitle',
    descriptionKey: 'workspace:publicSeo.pricingDesc',
  },
  '/checkout': {
    titleKey: 'workspace:publicSeo.checkoutTitle',
    descriptionKey: 'workspace:publicSeo.checkoutDesc',
  },
  '/contact-sales': {
    titleKey: 'workspace:publicSeo.contactSalesTitle',
    descriptionKey: 'workspace:publicSeo.contactSalesDesc',
  },
  '/terms': {
    titleKey: 'workspace:publicSeo.termsTitle',
    descriptionKey: 'workspace:publicSeo.termsDesc',
  },
  '/privacy': {
    titleKey: 'workspace:publicSeo.privacyTitle',
    descriptionKey: 'workspace:publicSeo.privacyDesc',
  },
  '/marketing-consent': {
    titleKey: 'workspace:publicSeo.marketingConsentTitle',
    descriptionKey: 'workspace:publicSeo.marketingConsentDesc',
  },
};

/* ------------------------------------------------------------------ */
/* Lookup helpers                                                      */
/* ------------------------------------------------------------------ */

const _modeToEntry = new Map<WorkspaceMode, RouteEntry>();
const _pathToEntry = new Map<string, RouteEntry>();

for (const entry of ROUTE_ENTRIES) {
  _modeToEntry.set(entry.mode, entry);
  _pathToEntry.set(entry.path, entry);
}

/** WorkspaceMode → RouteEntry */
export function getRouteByMode(mode: WorkspaceMode): RouteEntry | undefined {
  return _modeToEntry.get(mode);
}

/** URL path prefix → RouteEntry (첫 번째 매칭) */
export function getRouteByPath(pathname: string): RouteEntry | undefined {
  for (const entry of ROUTE_ENTRIES) {
    if (pathname === entry.path || pathname.startsWith(entry.path + '/')) {
      return entry;
    }
  }
  return undefined;
}

/** WorkspaceMode → URL path (id 없는 기본 경로) */
export function modeToPath(mode: WorkspaceMode): string {
  return _modeToEntry.get(mode)?.path ?? TENANT_DEFAULT_PATH;
}

/** WorkspaceMode + conversation id → URL path */
export function modeToPathWithId(mode: WorkspaceMode, id: string): string {
  const entry = _modeToEntry.get(mode);
  if (!entry) return TENANT_DEFAULT_PATH;
  return entry.hasIdParam ? `${entry.path}/${id}` : entry.path;
}

/* ------------------------------------------------------------------ */
/* DI registration — expose tenant-aware resolvers to shared layer.    */
/* Shared components (admin/billing/plugin/settings/sidebar/share/...) */
/* import `modeToPath` from `@auw/workspace-utils`, which delegates    */
/* back here after host bootstrap registers.                           */
/* ------------------------------------------------------------------ */

import {
  setModeToPath as _registerModeToPath,
  setModeToPathWithId as _registerModeToPathWithId,
  setGetRouteByPath as _registerGetRouteByPath,
  setExtractIdFromPath as _registerExtractIdFromPath,
} from '@auw/workspace-utils';

_registerModeToPath(modeToPath);
_registerModeToPathWithId(modeToPathWithId);
_registerGetRouteByPath(getRouteByPath);
_registerExtractIdFromPath(extractIdFromPath);

/** URL pathname에서 :id 파라미터 추출 */
export function extractIdFromPath(pathname: string): string | undefined {
  const entry = getRouteByPath(pathname);
  if (!entry?.hasIdParam) return undefined;
  const rest = pathname.slice(entry.path.length);
  if (!rest.startsWith('/')) return undefined;
  const id = rest.slice(1).split('/')[0];
  return id || undefined;
}

/** 기본 앱 이름 (SEO title prefix) — tenant manifest 기반. */
export const APP_NAME = activeTenant.identity.appName;

/** 전체 SEO title 포맷 */
export function formatSEOTitle(pageTitle?: string): string {
  return pageTitle ? `${pageTitle} | ${APP_NAME}` : APP_NAME;
}
